import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Bell, PencilLine } from 'lucide-react'
import CkEditorField from '../../components/CkEditorField'
import ConfirmDialog from '../../components/ConfirmDialog'
import { SwitchField } from '../../components/Form'
import FormPage from '../../components/FormPage'
import PageLoader from '../../components/PageLoader'
import { useToast } from '../../components/toastContext'
import { serverDetail, serverText } from '../../lib/i18n'
import { getSettings, saveNotificationTemplate } from './settingsService'
import { common as C, settingsStrings as S } from './strings'
import {
  documentDirection,
  documentGround,
  fillPlaceholders,
  findPlaceholders,
  sampleFor,
} from './templateDocument'
import './settings.css'

const T = S.notifications

/* Where the editor returns to: the list it was opened from. Plain `/settings` opened the
   media section, three groups away from the template that had just been saved. */
const LIST = '/settings#notifications'

/**
 * ONE NOTIFICATION TEMPLATE, on a page of its own — and the page is the message.
 *
 * A template is an email: a subject line, a sender, a recipient and a body. So that is
 * what the page shows, laid out the way a mail client lays a message out, with the body
 * editable in place. There is no form around it and no tab to go to for the result.
 *
 * The one control that is not the message is the switch in the head: SHOW SAMPLE VALUES.
 * On, every `__Placeholder` in the subject and the body is filled with a stand-in — the
 * message does not move, only its words change — and the body renders as the recipient's
 * mail client would render it. Off, it is the editor again, with the codes back.
 *
 * The editor is `CkEditorField` with the toolbar the phishing templates are authored
 * with: the legacy's own CKEditor build, the only one that carries `FullPage` (keeps the
 * doctype and `<head>` of a whole document) and `GeneralHtmlSupport` (keeps mail markup
 * intact). It stays MOUNTED while sample values show — unmounting it would destroy the
 * instance and its undo history on every glance at the result.
 *
 * Beside the message, the placeholders it uses — and clicking one puts it at the caret,
 * so the vocabulary is a palette, not only a legend.
 */
export default function NotificationTemplatePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [template, setTemplate] = useState(null)
  const [subject, setSubject] = useState('')
  const [content, setContent] = useState('')
  const [samples, setSamples] = useState(false)
  const [saving, setSaving] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const editor = useRef(null)

  useEffect(() => {
    let cancelled = false
    getSettings()
      .then((data) => {
        if (cancelled) return
        const found = data.notificationTemplates.find((row) => String(row.id) === String(id))
        if (!found) {
          /* Said as what it is. "This setting is not available yet" read as a feature
             still to come, for a template id that simply does not exist. */
          toast.error(T.missing)
          navigate(LIST, { replace: true })
          return
        }
        setTemplate(found)
        setSubject(found.subject)
        setContent(found.content)
      })
      .catch((error) => {
        if (cancelled) return
        toast.error(serverDetail(error?.message) || C.loadFailed)
        navigate(LIST, { replace: true })
      })
    return () => {
      cancelled = true
    }
    // Loads once for the id in the URL; the toast and navigate are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  /* Read once the template is here: the editor is built once and takes its direction
     then. Most templates are `<html lang="en">`, a Latin column left and an Arabic one
     right — edited on an RTL root those would swap, and the author would format a mirror
     image of what is sent. */
  const dir = useMemo(() => documentDirection(template?.content), [template])
  /* The template's own body colour, so the editable is the ground the message is read
     on — a colour that comes from the document being edited, not from this stylesheet. */
  const ground = useMemo(() => documentGround(content), [content])

  const placeholders = useMemo(() => findPlaceholders(`${subject}\n${content}`), [subject, content])
  const filledSubject = useMemo(() => fillPlaceholders(subject), [subject])
  const filledBody = useMemo(() => (samples ? fillPlaceholders(content) : ''), [samples, content])

  const dirty = template !== null && (subject !== template.subject || content !== template.content)
  const canSave = dirty && subject.trim() !== '' && content.trim() !== ''

  const save = useCallback(async () => {
    if (!canSave || saving) return
    setSaving(true)
    const outcome = await saveNotificationTemplate({ id: template.id, subject, content })
    setSaving(false)
    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || C.saveFailed)
      return
    }
    toast.success(serverText(outcome.message) || C.saved)
    navigate(LIST)
  }, [canSave, saving, template, subject, content, toast, navigate])

  /* Ctrl/⌘+S: the shortcut every editor teaches. Only when there is something to save,
     so it never steals the browser's own save-page over nothing. */
  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        if (!canSave) return
        event.preventDefault()
        save()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [canSave, save])

  /* Leaving with unsaved edits asks first. Back, Cancel and the X all come through here. */
  const leave = () => {
    if (dirty) setLeaving(true)
    else navigate(LIST)
  }

  /* A placeholder from the glossary goes in at the caret. If the sample view is showing,
     it comes off first — the codes have to be visible for one to be placed. */
  const insert = (token) => {
    const instance = editor.current
    if (!instance) return
    setSamples(false)
    instance.model.change((writer) => {
      instance.model.insertContent(writer.createText(token))
    })
    instance.editing.view.focus()
  }

  if (!template) return <PageLoader />

  return (
    <>
      <FormPage
        title={template.name}
        subtitle={template.lastUpdated ? `${T.lastUpdatedAt}: ${template.lastUpdated}` : T.editTitle}
        icon={Bell}
        onBack={leave}
        onSubmit={save}
        submitLabel={C.saveChanges}
        submitting={saving}
        submitDisabled={!canSave}
        actions={
          <div className="ntpl-switch">
            <SwitchField label={T.showSamples} checked={samples} onChange={setSamples} />
          </div>
        }
        aside={<Placeholders tokens={placeholders} onInsert={samples ? null : insert} />}
      >
        <article className={`ntpl-message${samples ? ' is-samples' : ''}`}>
          <div className="ntpl-envelope">
            <div className="ntpl-envelope__row">
              <span className="ntpl-envelope__key">{T.previewSubject}</span>
              {samples ? (
                <span className="ntpl-envelope__value ntpl-envelope__value--subject">
                  <bdi>{filledSubject || '—'}</bdi>
                </span>
              ) : (
                /* A line in the envelope, not a boxed field: the subject belongs to the
                   message. It is still an input — it just does not dress like one until
                   it has focus. */
                <label className="ntpl-subject">
                  <input
                    className="ntpl-subject__input"
                    dir="auto"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    aria-label={T.subject}
                  />
                  <PencilLine className="ntpl-subject__glyph" size={14} strokeWidth={2} aria-hidden="true" />
                </label>
              )}
            </div>
            <div className="ntpl-envelope__row">
              <span className="ntpl-envelope__key">{T.previewFrom}</span>
              <span className="ntpl-envelope__value">
                <bdi>{sampleFor('__App_Name_EN__')}</bdi>
              </span>
            </div>
            <div className="ntpl-envelope__row">
              <span className="ntpl-envelope__key">{T.previewTo}</span>
              <span className="ntpl-envelope__value">
                <bdi>{sampleFor('__USERNAME')}</bdi>
              </span>
            </div>
          </div>

          {/* Hidden, not unmounted — see above. */}
          <div className="ntpl-editor" hidden={samples} style={ground ? { '--ntpl-ground': ground } : undefined}>
            <CkEditorField
              value={content}
              onChange={setContent}
              minHeight={520}
              dir={dir}
              onReady={(instance) => {
                editor.current = instance
              }}
            />
          </div>

          {/* The body as a mail client renders it — its own document, so its own frame. An
              empty `sandbox` blocks scripts, forms and navigation: the preview shows the
              message, it never runs it. */}
          {samples && <iframe className="ntpl-frame" title={T.preview} sandbox="" srcDoc={filledBody} />}
        </article>
      </FormPage>

      <ConfirmDialog
        open={leaving}
        tone="warning"
        title={T.discardTitle}
        message={T.discardMessage}
        confirmLabel={T.discard}
        cancelLabel={T.keepEditing}
        onConfirm={() => navigate(LIST)}
        onCancel={() => setLeaving(false)}
      />
    </>
  )
}

/**
 * The vocabulary beside the message: what the sender puts where the template says
 * `__Course_EN`. Plain type — a glossary, not a panel — and each term is a button that
 * places itself at the caret while the editor is showing.
 */
function Placeholders({ tokens, onInsert }) {
  return (
    <div className="ntpl-glossary">
      <h2 className="ntpl-glossary__title">{T.placeholders}</h2>
      {tokens.length > 0 ? (
        <dl className="ntpl-glossary__list">
          {tokens.map((token) => {
            const sample = sampleFor(token)
            return (
              <div key={token}>
                <dt>
                  {onInsert ? (
                    <button
                      type="button"
                      className="ntpl-glossary__term"
                      dir="ltr"
                      title={T.insert}
                      onClick={() => onInsert(token)}
                    >
                      {token}
                    </button>
                  ) : (
                    <span className="ntpl-glossary__term ntpl-glossary__term--still" dir="ltr">
                      {token}
                    </span>
                  )}
                </dt>
                <dd className={sample ? undefined : 'ntpl-glossary__unknown'}>
                  <bdi>{sample ?? T.unknownPlaceholder}</bdi>
                </dd>
              </div>
            )
          })}
        </dl>
      ) : (
        <p className="ntpl-glossary__note">{T.noPlaceholders}</p>
      )}
      <p className="ntpl-glossary__note ntpl-glossary__note--rule">{T.previewHint}</p>
    </div>
  )
}
